import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { safeArray } from '../../utils/money';
import PaymentLinkService, {
  PaymentLink,
  PaymentLinkRequest,
} from '../../services/paymentLinks';

interface PaymentLinkState {
  paymentLinks: {
    data: PaymentLink[];
    meta: any;
    loading: boolean;
  };
  currentPaymentLink: PaymentLink | null;
  creating: boolean;
  updating: boolean;
  error: string | null;
}

const initialState: PaymentLinkState = {
  paymentLinks: {
    data: [],
    meta: null,
    loading: false,
  },
  currentPaymentLink: null,
  creating: false,
  updating: false,
  error: null,
};

export const fetchPaymentLinks = createAsyncThunk(
  'paymentLink/fetchPaymentLinks',
  async (
    params: { page?: number; limit?: number; filters?: { active?: boolean } },
    { rejectWithValue }
  ) => {
    try {
      return await PaymentLinkService.listPaymentLinks(
        params.page,
        params.limit,
        params.filters
      );
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch payment links'
      );
    }
  }
);

export const fetchPaymentLinkDetail = createAsyncThunk(
  'paymentLink/fetchPaymentLinkDetail',
  async (id: string, { rejectWithValue }) => {
    try {
      return await PaymentLinkService.getPaymentLink(id);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch payment link'
      );
    }
  }
);

export const createPaymentLink = createAsyncThunk(
  'paymentLink/createPaymentLink',
  async (data: PaymentLinkRequest, { rejectWithValue }) => {
    try {
      return await PaymentLinkService.createPaymentLink(data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create payment link'
      );
    }
  }
);

export const updatePaymentLink = createAsyncThunk(
  'paymentLink/updatePaymentLink',
  async (
    { id, data }: { id: string; data: Partial<PaymentLinkRequest> },
    { rejectWithValue }
  ) => {
    try {
      return await PaymentLinkService.updatePaymentLink(id, data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update payment link'
      );
    }
  }
);

export const deletePaymentLink = createAsyncThunk(
  'paymentLink/deletePaymentLink',
  async (id: string, { rejectWithValue }) => {
    try {
      await PaymentLinkService.deletePaymentLink(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete payment link'
      );
    }
  }
);

export const activatePaymentLink = createAsyncThunk(
  'paymentLink/activatePaymentLink',
  async (id: string, { rejectWithValue }) => {
    try {
      return await PaymentLinkService.activatePaymentLink(id);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to activate payment link'
      );
    }
  }
);

export const deactivatePaymentLink = createAsyncThunk(
  'paymentLink/deactivatePaymentLink',
  async (id: string, { rejectWithValue }) => {
    try {
      return await PaymentLinkService.deactivatePaymentLink(id);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to deactivate payment link'
      );
    }
  }
);

const paymentLinkSlice = createSlice({
  name: 'paymentLink',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentPaymentLink: (state) => {
      state.currentPaymentLink = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Payment Links
    builder
      .addCase(fetchPaymentLinks.pending, (state) => {
        state.paymentLinks.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPaymentLinks.fulfilled,
        (state, action: PayloadAction<{ data: PaymentLink[]; meta: any }>) => {
          state.paymentLinks.loading = false;
          state.paymentLinks.data = safeArray<PaymentLink>(action.payload.data);
          state.paymentLinks.meta = action.payload.meta;
        }
      )
      .addCase(fetchPaymentLinks.rejected, (state, action) => {
        state.paymentLinks.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Payment Link Detail
    builder
      .addCase(fetchPaymentLinkDetail.pending, (state) => {
        state.paymentLinks.loading = true;
      })
      .addCase(
        fetchPaymentLinkDetail.fulfilled,
        (state, action: PayloadAction<PaymentLink>) => {
          state.paymentLinks.loading = false;
          state.currentPaymentLink = action.payload;
        }
      )
      .addCase(fetchPaymentLinkDetail.rejected, (state, action) => {
        state.paymentLinks.loading = false;
        state.error = action.payload as string;
      });

    // Create Payment Link
    builder
      .addCase(createPaymentLink.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(
        createPaymentLink.fulfilled,
        (state, action: PayloadAction<PaymentLink>) => {
          state.creating = false;
          state.paymentLinks.data.unshift(action.payload);
          state.currentPaymentLink = action.payload;
        }
      )
      .addCase(createPaymentLink.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      });

    // Update Payment Link
    builder
      .addCase(updatePaymentLink.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(
        updatePaymentLink.fulfilled,
        (state, action: PayloadAction<PaymentLink>) => {
          state.updating = false;
          const index = state.paymentLinks.data.findIndex(
            (link) => link.id === action.payload.id
          );
          if (index !== -1) {
            state.paymentLinks.data[index] = action.payload;
          }
          if (state.currentPaymentLink?.id === action.payload.id) {
            state.currentPaymentLink = action.payload;
          }
        }
      )
      .addCase(updatePaymentLink.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload as string;
      });

    // Delete Payment Link
    builder
      .addCase(deletePaymentLink.pending, (state) => {
        state.updating = true;
      })
      .addCase(deletePaymentLink.fulfilled, (state, action: PayloadAction<string>) => {
        state.updating = false;
        state.paymentLinks.data = state.paymentLinks.data.filter(
          (link) => link.id !== action.payload
        );
        if (state.currentPaymentLink?.id === action.payload) {
          state.currentPaymentLink = null;
        }
      })
      .addCase(deletePaymentLink.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload as string;
      });

    // Activate Payment Link
    builder
      .addCase(activatePaymentLink.pending, (state) => {
        state.updating = true;
      })
      .addCase(
        activatePaymentLink.fulfilled,
        (state, action: PayloadAction<PaymentLink>) => {
          state.updating = false;
          const index = state.paymentLinks.data.findIndex(
            (link) => link.id === action.payload.id
          );
          if (index !== -1) {
            state.paymentLinks.data[index] = action.payload;
          }
          if (state.currentPaymentLink?.id === action.payload.id) {
            state.currentPaymentLink = action.payload;
          }
        }
      )
      .addCase(activatePaymentLink.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload as string;
      });

    // Deactivate Payment Link
    builder
      .addCase(deactivatePaymentLink.pending, (state) => {
        state.updating = true;
      })
      .addCase(
        deactivatePaymentLink.fulfilled,
        (state, action: PayloadAction<PaymentLink>) => {
          state.updating = false;
          const index = state.paymentLinks.data.findIndex(
            (link) => link.id === action.payload.id
          );
          if (index !== -1) {
            state.paymentLinks.data[index] = action.payload;
          }
          if (state.currentPaymentLink?.id === action.payload.id) {
            state.currentPaymentLink = action.payload;
          }
        }
      )
      .addCase(deactivatePaymentLink.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentPaymentLink } = paymentLinkSlice.actions;

export default paymentLinkSlice.reducer;
